import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import {
    Box, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
} from '@mui/material'
import { tone, ui } from '../../../../utils/uiTokens'
import {
    FACTOR_META, money, TONE_COLORS, TONE_SOFT, toneOf,
} from '../../../../utils/salaryFormulas'
import { MentorSalaryDetail, SalaryFactor, StudentSalaryRow } from '../../../../features/salary/salarySlice'

const AVATARS = [ui.pink, tone.info.text, tone.ok.text, tone.warn.text, ui.sub]

interface Props {
    detail: MentorSalaryDetail
    editable: boolean
    onSaveManual?: (enrollId: number, factor: string, percent: number) => void
    initialGroup?: string
    // Недельный срез: только проценты, без денег, с колонкой «Апта қорытындысы»
    weekMode?: boolean
    weekNumber?: number
}

// Общий подробный вид зарплаты (админ-детальная и кабинет ментора).
const SalaryDetailView: React.FC<Props> = ({ detail, editable, onSaveManual, initialGroup = '', weekMode = false, weekNumber = 0 }) => {
    const { formatMessage } = useIntl()
    const students = detail.students ?? []
    const count = detail.studentCount || students.length || 0
    const avg = count ? Math.round(detail.total / count) : 0
    const sharePct = detail.maxTotal ? Math.round((detail.total / detail.maxTotal) * 100) : 0

    const [group, setGroup] = useState(initialGroup)
    const groups = useMemo(
        () => Array.from(new Set(students.map((s) => s.groupTitle).filter(Boolean))).sort(),
        [students],
    )
    const visible = useMemo(
        () => (group ? students.filter((s) => s.groupTitle === group) : students),
        [students, group],
    )
    const groupTotal = visible.reduce((sum, s) => sum + (s.total ?? 0), 0)

    // Средний «академический» показатель (видео/тест/ПС/зачёт), для легенды
    const avgPerf = useMemo(() => {
        if (!visible.length) return 0
        const per = visible.map((s) => rowPerf(s))
        return Math.round(per.reduce((a, b) => a + b, 0) / per.length)
    }, [visible])

    return (
        <>
            <Box sx={cycleStrip}>
                <Box sx={{ fontWeight: 700, color: ui.ink }}>
                    {detail.cycleNumber} {formatMessage({ id: 'month' })}
                    <Box component="span" sx={{ marginLeft: '10px', ...pill(detail.cycle?.status === 'CLOSED' ? 'off' : 'ok') }}>
                        {formatMessage({ id: detail.cycle?.status === 'CLOSED' ? 'salaryStatusClosed' : 'salaryStatusOpen' })}
                    </Box>
                </Box>
                <Box sx={{ color: ui.muted, fontSize: '12px', maxWidth: 420, textAlign: 'right' }}>
                    {formatMessage({ id: 'salaryCycleNote' })}
                </Box>
            </Box>

            {!weekMode && (
                <Box sx={kpiRow}>
                    <Kpi label={formatMessage({ id: 'salaryMonthTotal' })} value={money(detail.total)} dark />
                    <Kpi label={formatMessage({ id: 'salaryPerStudentAvg' })} value={money(avg)} />
                    <Kpi label={formatMessage({ id: 'salaryShareOfMax' })} value={`${sharePct}%`} accent={tone.ok.text} />
                    <Kpi label={formatMessage({ id: 'salaryStudentCount' })} value={`${count}`} />
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, marginTop: '22px' }}>
                <Box sx={{ fontWeight: 700, fontSize: '16px', color: ui.ink }}>
                    {formatMessage({ id: 'salaryStudentsDetail' })}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <LegendChip tone="ok" label={formatMessage({ id: 'salaryGood' })} />
                    <LegendChip tone="warn" label={formatMessage({ id: 'salaryMid' })} />
                    <LegendChip tone="bad" label={formatMessage({ id: 'salaryBehind' })} />
                </Box>
            </Box>

            {!weekMode && (
                <Box sx={banner}>
                    <Box>
                        <Box sx={{ fontSize: '11.5px', letterSpacing: '0.06em', color: ui.pinkDark, textTransform: 'uppercase' }}>
                            {formatMessage({ id: 'salaryTotalBanner' })}
                        </Box>
                        <Box sx={{ color: ui.sub, fontSize: '13px', marginTop: '6px' }}>
                            {count} {formatMessage({ id: 'colStudents' })} × {money(avg)} · {formatMessage({ id: 'salaryPerStudentMax' })}
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Box sx={{ fontSize: '30px', fontWeight: 800, color: ui.pinkDark, lineHeight: 1.1 }}>{money(detail.total)}</Box>
                        <Box sx={{ color: ui.sub, fontSize: '12px' }}>/ {money(detail.maxTotal)} {formatMessage({ id: 'salaryMax' })}</Box>
                    </Box>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, margin: '10px 2px' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {groups.length > 1 && ['', ...groups].map((g) => (
                        <Chip
                            key={g || 'all'}
                            size="small"
                            label={g || formatMessage({ id: 'allGroups' })}
                            onClick={() => setGroup(g)}
                            sx={{
                                fontWeight: 600,
                                backgroundColor: group === g ? ui.pink : ui.track,
                                color: group === g ? '#fff' : ui.sub,
                                '&:hover': { backgroundColor: group === g ? ui.pinkDark : ui.border },
                            }}
                        />
                    ))}
                </Box>
                <Box sx={{ color: ui.sub, fontSize: '12.5px' }}>
                    {formatMessage({ id: 'salaryShownCount' }, { count: visible.length })} · {formatMessage({ id: 'salaryAvgLabel' })}: <b>{avgPerf}%</b>
                    {!weekMode && group && <> · {formatMessage({ id: 'salaryGroupTotal' })}: <b>{money(groupTotal)}</b></>}
                </Box>
            </Box>

            <TableContainer sx={tableWrap}>
                <Table>
                    <TableHead>
                        <TableRow sx={headRow}>
                            <TableCell sx={{ width: 44 }}>#</TableCell>
                            <TableCell>{formatMessage({ id: 'colStudent' })}</TableCell>
                            {FACTOR_META.map((f) => (
                                <TableCell key={f.key} align="center">
                                    {formatMessage({ id: f.labelId })}{!weekMode && ` · ${money(maxOf(f.key, detail))}`}
                                </TableCell>
                            ))}
                            <TableCell align="right">{formatMessage({ id: weekMode ? 'salaryWeekSummary' : 'salaryStudentSum' })}</TableCell>
                            <TableCell align="center" sx={{ width: 70 }}>{formatMessage({ id: 'salaryDetails' })}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map((s, i) => (
                            <StudentRows
                                key={s.userSubjectEnrollId}
                                student={s}
                                index={i}
                                editable={editable}
                                cycleClosed={detail.cycle?.status === 'CLOSED'}
                                onSaveManual={onSaveManual}
                                weekMode={weekMode}
                                weekNumber={weekNumber}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

const StudentRows: React.FC<{
    student: StudentSalaryRow
    index: number
    editable: boolean
    cycleClosed: boolean
    onSaveManual?: (enrollId: number, factor: string, percent: number) => void
    weekMode?: boolean
    weekNumber?: number
}> = ({ student, index, editable, cycleClosed, onSaveManual, weekMode = false, weekNumber = 0 }) => {
    const { formatMessage } = useIntl()
    const [open, setOpen] = useState(false)
    const byKey = useMemo(() => {
        const m: Record<string, SalaryFactor> = {}
        student.factors.forEach((f) => (m[f.key] = f))
        return m
    }, [student])

    const perf = rowPerf(student)
    const perfTone: 'ok' | 'warn' | 'bad' = weekMode
        ? (perf >= 80 ? 'ok' : perf >= 50 ? 'warn' : 'bad')
        : (perf >= 75 ? 'ok' : perf >= 50 ? 'warn' : 'bad')
    const share = student.maxTotal ? Math.round((student.total / student.maxTotal) * 100) : 0
    const initials = `${(student.firstName || '').trim()[0] || ''}${(student.lastName || '').trim()[0] || ''}`.toUpperCase()
    const canEdit = editable && !cycleClosed

    return (
        <>
            <TableRow sx={{ ...bodyRow, borderLeft: `4px solid ${TONE_COLORS[perfTone]}` }}>
                <TableCell>
                    <Box sx={{ ...rankBadge, backgroundColor: TONE_SOFT[perfTone], color: TONE_COLORS[perfTone] }}>{index + 1}</Box>
                </TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{ ...avatar, backgroundColor: AVATARS[index % AVATARS.length] }}>{initials}</Box>
                        <Box>
                            <Box sx={{ fontWeight: 600, color: ui.ink }}>
                                {`${student.firstName ?? ''} ${student.lastName ?? ''}`.trim()}
                            </Box>
                            <Box sx={{ color: ui.muted, fontSize: '11.5px' }}>
                                {weekMode && weekNumber ? `${weekNumber}-${formatMessage({ id: 'week' })} · ` : ''}{student.groupTitle}
                            </Box>
                        </Box>
                    </Box>
                </TableCell>

                {FACTOR_META.map((meta) => {
                    const f = byKey[meta.key]
                    const pct = f?.inputPercent ?? 0
                    // В недельном режиме опрос считается раз в месяц — показываем «ай соңында»
                    if (weekMode && meta.key === 'OPROS') {
                        return (
                            <TableCell key={meta.key} align="center">
                                <Box sx={{ ...factorPct, backgroundColor: ui.track, color: ui.muted }}>
                                    {formatMessage({ id: 'salaryAtMonthEnd' })}
                                </Box>
                            </TableCell>
                        )
                    }
                    const tone: 'ok' | 'warn' | 'bad' = weekMode
                        ? (pct >= 75 ? 'ok' : pct >= 50 ? 'warn' : 'bad')
                        : toneOf(f?.amount ?? 0, f?.maxAmount ?? 1)
                    const cnt = f && f.totalCount ? ` (${f.doneCount}/${f.totalCount})` : ''
                    return (
                        <TableCell key={meta.key} align="center">
                            <Box sx={{ ...factorPct, backgroundColor: TONE_SOFT[tone], color: TONE_COLORS[tone] }}>
                                {pct}%{cnt}
                            </Box>
                            {!weekMode && (
                                <Box sx={{ fontWeight: 700, fontSize: '13px', color: ui.ink, marginTop: '4px' }}>
                                    {money(f?.amount)}
                                    {f?.manualOverride && <Box component="span" sx={{ color: TONE_COLORS.warn, marginLeft: '3px' }}>✎</Box>}
                                </Box>
                            )}
                        </TableCell>
                    )
                })}

                <TableCell align="right">
                    <Box sx={{ fontWeight: 800, fontSize: '15px', color: TONE_COLORS[perfTone] }}>
                        {weekMode ? `${perf}%` : money(student.total)}
                    </Box>
                    <Box sx={{ color: ui.muted, fontSize: '11px' }}>
                        {weekMode ? formatMessage({ id: 'salaryWeekIndicator' }) : `/ ${money(student.maxTotal)} · ${share}%`}
                    </Box>
                </TableCell>
                <TableCell align="center">
                    <Button
                        onClick={() => setOpen((v) => !v)}
                        sx={{ minWidth: 36, height: 36, borderRadius: '10px', backgroundColor: ui.pinkSoft, color: ui.pink, fontSize: '18px', lineHeight: 1 }}
                    >
                        {open ? '×' : '→'}
                    </Button>
                </TableCell>
            </TableRow>

            {open && (
                <TableRow>
                    <TableCell colSpan={FACTOR_META.length + 4} sx={{ backgroundColor: ui.surface, borderBottom: `1px solid ${ui.border}` }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '6px 4px' }}>
                            {FACTOR_META.map((meta) => (
                                <Box key={meta.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                    <Box sx={{ minWidth: 160, fontWeight: 600, color: ui.ink }}>{formatMessage({ id: meta.labelId })}</Box>
                                    <Box sx={{ flexGrow: 1, color: ui.sub, fontSize: '12.5px' }}>{formatMessage({ id: meta.ruleId })}</Box>
                                    <Box sx={{ fontWeight: 700, color: ui.ink, minWidth: 90, textAlign: 'right' }}>
                                        {byKey[meta.key]?.inputPercent ?? 0}%{!weekMode && ` · ${money(byKey[meta.key]?.amount)}`}
                                    </Box>
                                </Box>
                            ))}
                            {canEdit && (
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', marginTop: '6px', paddingTop: '10px', borderTop: `1px dashed ${ui.border}` }}>
                                    <Box sx={{ fontSize: '12.5px', color: ui.sub }}>{formatMessage({ id: 'salaryManualEdit' })}:</Box>
                                    <ManualInput
                                        label={formatMessage({ id: 'factorPs' })}
                                        defaultValue={byKey.PS?.inputPercent ?? 0}
                                        onSave={(v) => onSaveManual?.(student.userSubjectEnrollId, 'PS', v)}
                                    />
                                    <ManualInput
                                        label={formatMessage({ id: 'factorZachet' })}
                                        defaultValue={byKey.ZACHET?.inputPercent ?? 0}
                                        onSave={(v) => onSaveManual?.(student.userSubjectEnrollId, 'ZACHET', v)}
                                    />
                                </Box>
                            )}
                        </Box>
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}

const ManualInput: React.FC<{ label: string; defaultValue: number; onSave: (v: number) => void }> = ({ label, defaultValue, onSave }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ fontSize: '12.5px', color: ui.ink }}>{label}</Box>
        <TextField
            size="small"
            type="number"
            defaultValue={defaultValue}
            onBlur={(e) => onSave(clampPct(Number(e.target.value)))}
            inputProps={{ min: 0, max: 100, style: { width: 52, textAlign: 'center' } }}
        />
        <Box sx={{ color: ui.muted }}>%</Box>
    </Box>
)

const Kpi: React.FC<{ label: string; value: string; dark?: boolean; accent?: string }> = ({ label, value, dark, accent }) => (
    <Box sx={{ border: `1px solid ${dark ? ui.pinkSoft : ui.border}`, backgroundColor: dark ? ui.pinkSoft : ui.card, borderRadius: '14px', padding: '16px 18px' }}>
        <Box sx={{ fontSize: '12px', color: dark ? ui.pinkDark : ui.sub, marginBottom: '8px' }}>{label}</Box>
        <Box sx={{ fontSize: '22px', fontWeight: 700, color: dark ? ui.pinkDark : accent || ui.ink }}>{value}</Box>
    </Box>
)

const LegendChip: React.FC<{ tone: 'ok' | 'warn' | 'bad'; label: string }> = ({ tone, label }) => (
    <Box sx={{ padding: '5px 10px', borderRadius: '7px', fontWeight: 700, fontSize: '11px', backgroundColor: TONE_SOFT[tone], color: TONE_COLORS[tone] }}>
        {label}
    </Box>
)

const rowPerf = (s: StudentSalaryRow) => {
    const keys = ['VIDEO', 'TEST', 'PS', 'ZACHET']
    const vals = keys.map((k) => s.factors.find((f) => f.key === k)?.inputPercent ?? 0)
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
}

const maxOf = (key: string, detail: MentorSalaryDetail) => {
    for (const s of detail.students ?? []) {
        const f = s.factors.find((x) => x.key === key)
        if (f) return f.maxAmount
    }
    const fallback: Record<string, number> = { VIDEO: 1500, TEST: 400, PS: 400, ZACHET: 400, OPROS: 300 }
    return fallback[key] ?? 0
}

const clampPct = (n: number) => Math.max(0, Math.min(100, Math.round(Number.isFinite(n) ? n : 0)))

const pill = (kind: 'ok' | 'off') => ({
    display: 'inline-block',
    padding: '3px 9px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 700,
    backgroundColor: kind === 'ok' ? tone.ok.bg : ui.track,
    color: kind === 'ok' ? tone.ok.text : ui.sub,
})

const cycleStrip = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    border: `1px solid ${ui.border}`,
    borderLeft: `3px solid ${ui.pink}`,
    borderRadius: ui.radius,
    padding: '12px 16px',
    marginBottom: '18px',
}

const kpiRow = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '14px',
    '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (max-width: 520px)': { gridTemplateColumns: '1fr' },
}

const banner = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    backgroundColor: ui.pinkSoft,
    border: `1px solid ${ui.border}`,
    borderRadius: '16px',
    padding: '18px 22px',
    marginTop: '14px',
}

const tableWrap = { border: `1px solid ${ui.border}`, borderRadius: '16px', overflow: 'hidden' }

const headRow = {
    '& .MuiTableCell-root': {
        backgroundColor: ui.surface,
        borderBottom: `1px solid ${ui.border}`,
        color: ui.sub,
        fontSize: '11.5px',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
}

const bodyRow = { '& .MuiTableCell-root': { borderBottom: `1px solid ${ui.border}`, fontSize: '13px' } }

const rankBadge = {
    width: 26,
    height: 26,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '12px',
}

const avatar = {
    width: 34,
    height: 34,
    borderRadius: '50%',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '12px',
    flexShrink: 0,
}

const factorPct = {
    display: 'inline-block',
    padding: '3px 9px',
    borderRadius: '6px',
    fontWeight: 700,
    fontSize: '11.5px',
    whiteSpace: 'nowrap',
}

export default SalaryDetailView
